<template lang="pug">
.camp-detail(v-if="camp")
  .page-header
    h1 {{ camp.name }}
    .camp-meta
      span.site {{ getSiteDisplay(camp.site) }}
      span.altitude {{ getAltitudeDisplay(camp.altitude) }}

  .content-wrapper
    .main-content
      .info-section
        .info-item(v-if="camp.address")
          i.el-icon-location
          span {{ camp.address }}
        .info-item(v-if="camp.phone")
          i.el-icon-phone
          span {{ camp.phone }}
        .info-item(v-if="camp.area")
          i.el-icon-map-location
          span {{ camp.area }} - {{ camp.city }} {{ camp.district }}

      .tags-section(v-if="camp.tags && camp.tags.length")
        h3 營地特色
        .tags-container
          el-tag(
            v-for="tag in getUniqueTags(camp.tags)"
            :key="tag"
            size="small"
            class="tag-item"
          ) {{ tag }}

      .video-section(v-if="camp.video_ids && camp.video_ids.length")
        h3 相關影片
        .video-container
          iframe(
            v-for="(videoId, index) in camp.video_ids.slice(0, 2)"
            :key="videoId"
            :src="`https://www.youtube.com/embed/${videoId}`"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            :title="`營地影片 ${index + 1}`"
          )

      .description-section(v-if="camp.description")
        h3 營地介紹
        p {{ camp.description }}

      .guidelines-section(v-if="camp.guideline")
        h3 注意事項
        p {{ camp.guideline }}

    .side-content
      .spaces-section(v-if="camp.spaces && camp.spaces.length")
        h3 營位資訊
        .space-list
          .space-item(v-for="space in camp.spaces" :key="space.name")
            .space-header
              h4 {{ space.name }}
              span.unit {{ space.unit }}
            .space-info
              .price-info
                .price-item
                  span.label 平日
                  span.value {{ space.price_weekday }}元
                .price-item
                  span.label 週末
                  span.value {{ space.price_weekend }}元
                .price-item
                  span.label 連假
                  span.value {{ space.price_holiday }}元
              .space-description {{ space.description }}
            .space-footer
              span.max-total 最多{{ space.default_max_total }}{{ space.unit }}

</template>

<script>
import { API_ENDPOINTS } from '../config/api'
import { getToken } from '../utils/token'

export default {
  name: 'CampDetail',
  data() {
    return {
      camp: null,
      loading: false
    }
  },
  created() {
    this.fetchCampDetail()
  },
  methods: {
    async fetchCampDetail() {
      this.loading = true
      try {
        const { site, id } = this.$route.params
        const token = getToken()
        const response = await fetch(`${API_ENDPOINTS.CAMP_BASE}/${site}/${id}/detail`, {
          headers: {
            'X-Validation': token
          }
        })
        const data = await response.json()
        this.camp = data
        document.title = `${data.name} - 台灣露營地搜尋`
      } catch (error) {
        console.error('獲取營地詳情失敗:', error)
        this.$message.error('獲取營地詳情失敗')
      } finally {
        this.loading = false
      }
    },
    getAltitudeDisplay(altitude) {
      if (altitude === 0) return '未知'
      if (altitude === -1) return '-'
      return `${altitude}m`
    },
    getSiteDisplay(site) {
      const siteMap = {
        icamping: '愛露營',
        easycamp: '露營樂',
        twcamping: '台灣露營地訂位中心'
      }
      return siteMap[site] || site
    },
    getUniqueTags(tags) {
      if (!tags) return []
      return [...new Set(tags)]
    }
  }
}
</script>

<style scoped>
.camp-detail {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-header h1 {
  font-size: 32px;
  color: #303133;
  margin-bottom: 16px;
}

.camp-meta {
  display: flex;
  justify-content: center;
  gap: 20px;
  color: #606266;
}

.content-wrapper {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
}

.main-content, .side-content {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

h3 {
  font-size: 20px;
  color: #303133;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #409EFF;
}

.info-section {
  margin-bottom: 24px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  color: #606266;
}

.info-item i {
  font-size: 18px;
  color: #409EFF;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
}

.tag-item {
  margin-right: 8px;
  margin-bottom: 8px;
}

.video-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.video-container iframe {
  width: 100%;
  height: 200px;
  border-radius: 8px;
}

.description-section, .guidelines-section {
  margin-bottom: 24px;
}

.description-section p, .guidelines-section p {
  color: #606266;
  line-height: 1.6;
  white-space: pre-line;
}

.space-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.space-item {
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  padding: 16px;
}

.space-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.space-header h4 {
  font-size: 16px;
  color: #303133;
  margin: 0;
}

.unit {
  color: #909399;
  font-size: 14px;
}

.price-info {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 12px;
}

.price-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.price-item .label {
  font-size: 12px;
  color: #909399;
}

.price-item .value {
  font-size: 16px;
  color: #303133;
  font-weight: bold;
}

.space-description {
  color: #606266;
  font-size: 14px;
  margin-bottom: 12px;
  white-space: pre-line;
}

.space-footer {
  text-align: right;
  color: #909399;
  font-size: 14px;
}

@media (max-width: 768px) {
  .camp-detail {
    padding: 16px;
  }

  .page-header h1 {
    font-size: 24px;
  }

  .content-wrapper {
    grid-template-columns: 1fr;
  }

  .video-container {
    grid-template-columns: 1fr;
  }
}
</style> 